class UserPhotoService {
  constructor() {
    this.basePath = '/media/soliveira/linux-files1/dev/projects/authsystem/frontend/src/user-preferences/photos';
    this.storageKey = 'user_photos';
    this.maxFileSize = 5 * 1024 * 1024; // 5MB
    this.allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    this.photoCache = new Map(); // Cache para melhorar performance
  }

  /**
   * Gera o nome do arquivo baseado no ID do usuário
   * @param {string} userId - ID único do usuário
   * @returns {string} Nome do arquivo
   */
  generateFileName(userId) {
    const timestamp = new Date().getTime();
    return `${userId}_${timestamp}.json`;
  }

  /**
   * Gera o caminho completo do arquivo
   * @param {string} userId - ID único do usuário
   * @returns {string} Caminho completo do arquivo
   */
  generateFilePath(userId) {
    return `${this.basePath}/${this.generateFileName(userId)}`;
  }

  /**
   * Valida um arquivo de imagem
   * @param {File} file - Arquivo a ser validado
   * @returns {Object} Resultado da validação
   */
  validateImageFile(file) {
    const errors = [];

    if (!file) {
      errors.push('Nenhum arquivo selecionado');
      return { isValid: false, errors };
    }

    // Verificar tipo de arquivo
    if (!this.allowedTypes.includes(file.type)) {
      errors.push(`Formato não suportado: ${file.type}. Use: ${this.allowedTypes.join(', ')}`);
    }

    // Verificar tamanho
    if (file.size > this.maxFileSize) {
      const maxSizeMB = this.maxFileSize / (1024 * 1024);
      errors.push(`Arquivo muito grande: ${(file.size / (1024 * 1024)).toFixed(2)}MB. Máximo: ${maxSizeMB}MB`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Comprime uma imagem para melhorar performance
   * @param {File} file - Arquivo de imagem
   * @param {number} maxWidth - Largura máxima
   * @param {number} maxHeight - Altura máxima
   * @param {number} quality - Qualidade da compressão (0-1)
   * @returns {Promise<Blob>} Imagem comprimida
   */
  async compressImage(file, maxWidth = 800, maxHeight = 800, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calcular dimensões mantendo proporção
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Configurar canvas
        canvas.width = width;
        canvas.height = height;

        // Desenhar imagem comprimida
        ctx.drawImage(img, 0, 0, width, height);

        // Converter para blob
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Converte arquivo para base64
   * @param {File} file - Arquivo a ser convertido
   * @returns {Promise<string>} String base64
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Salva a foto de um usuário
   * @param {string} userId - ID do usuário
   * @param {File} file - Arquivo de imagem
   * @param {Object} options - Opções de compressão
   * @returns {Promise<Object>} Dados da foto salva
   */
  async saveUserPhoto(userId, file, options = {}) {
    try {
      // Validar arquivo
      const validation = this.validateImageFile(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Comprimir imagem se necessário
      const compressedFile = options.compress !== false 
        ? await this.compressImage(file, options.maxWidth, options.maxHeight, options.quality)
        : file;

      // Converter para base64
      const base64Data = await this.fileToBase64(compressedFile);

      // Criar objeto de dados da foto
      const photoData = {
        userId,
        photoUrl: base64Data,
        fileName: file.name,
        originalSize: file.size,
        compressedSize: compressedFile.size,
        uploadDate: new Date().toISOString(),
        mimeType: file.type,
        dimensions: options.dimensions || null,
        version: '1.0.0'
      };

      // Salvar no localStorage (simulação)
      const storageKey = `user_photo_${userId}`;
      localStorage.setItem(storageKey, JSON.stringify(photoData));

      // Adicionar ao cache
      this.photoCache.set(userId, photoData);

      // Log da operação
      console.log(`✅ Foto salva para usuário ${userId}:`, {
        originalSize: `${(file.size / 1024).toFixed(2)}KB`,
        compressedSize: `${(compressedFile.size / 1024).toFixed(2)}KB`,
        compressionRatio: `${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`
      });

      return photoData;
    } catch (error) {
      console.error('❌ Erro ao salvar foto:', error);
      throw error;
    }
  }

  /**
   * Carrega a foto de um usuário
   * @param {string} userId - ID do usuário
   * @returns {Promise<Object|null>} Dados da foto ou null
   */
  async loadUserPhoto(userId) {
    try {
      // Verificar cache primeiro
      if (this.photoCache.has(userId)) {
        return this.photoCache.get(userId);
      }

      const storageKey = `user_photo_${userId}`;
      const storedPhoto = localStorage.getItem(storageKey);

      if (!storedPhoto) {
        return null;
      }

      const photoData = JSON.parse(storedPhoto);
      
      // Validar dados
      if (!photoData.userId || !photoData.photoUrl) {
        console.warn(`⚠️ Dados de foto inválidos para usuário ${userId}`);
        return null;
      }

      // Adicionar ao cache
      this.photoCache.set(userId, photoData);

      console.log(`✅ Foto carregada para usuário ${userId}`);
      return photoData;
    } catch (error) {
      console.error('❌ Erro ao carregar foto:', error);
      return null;
    }
  }

  /**
   * Remove a foto de um usuário
   * @param {string} userId - ID do usuário
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async removeUserPhoto(userId) {
    try {
      const storageKey = `user_photo_${userId}`;
      localStorage.removeItem(storageKey);

      // Remover do cache
      this.photoCache.delete(userId);

      console.log(`🗑️ Foto removida para usuário ${userId}`);
      return true;
    } catch (error) {
      console.error('❌ Erro ao remover foto:', error);
      return false;
    }
  }

  /**
   * Atualiza a foto de um usuário
   * @param {string} userId - ID do usuário
   * @param {File} file - Nova foto
   * @param {Object} options - Opções de compressão
   * @returns {Promise<Object>} Dados da foto atualizada
   */
  async updateUserPhoto(userId, file, options = {}) {
    try {
      // Primeiro, carrega a foto existente
      const existingPhoto = await this.loadUserPhoto(userId);
      
      if (!existingPhoto) {
        // Se não existir, cria uma nova
        return await this.saveUserPhoto(userId, file, options);
      }

      // Remove a foto antiga
      await this.removeUserPhoto(userId);

      // Salva a nova foto
      return await this.saveUserPhoto(userId, file, options);
    } catch (error) {
      console.error('❌ Erro ao atualizar foto:', error);
      throw error;
    }
  }

  /**
   * Lista todos os usuários com fotos salvas
   * @returns {Promise<Array>} Lista de usuários
   */
  async listUsersWithPhotos() {
    try {
      const users = [];
      
      // Itera sobre todas as chaves do localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        
        if (key && key.startsWith('user_photo_')) {
          const userId = key.replace('user_photo_', '');
          const photoData = await this.loadUserPhoto(userId);
          
          if (photoData) {
            users.push({
              userId,
              uploadDate: photoData.uploadDate,
              fileName: photoData.fileName,
              fileSize: photoData.compressedSize || photoData.originalSize
            });
          }
        }
      }

      return users;
    } catch (error) {
      console.error('❌ Erro ao listar usuários com fotos:', error);
      return [];
    }
  }

  /**
   * Verifica se um usuário tem foto salva
   * @param {string} userId - ID do usuário
   * @returns {Promise<boolean>} True se existir, false caso contrário
   */
  async hasUserPhoto(userId) {
    try {
      const photo = await this.loadUserPhoto(userId);
      return photo !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Obtém estatísticas das fotos
   * @returns {Promise<Object>} Estatísticas das fotos
   */
  async getPhotoStats() {
    try {
      const users = await this.listUsersWithPhotos();
      const stats = {
        totalUsers: users.length,
        totalSize: 0,
        averageSize: 0,
        lastUpload: null,
        fileTypes: {}
      };

      users.forEach(user => {
        stats.totalSize += user.fileSize;
        
        if (!stats.lastUpload || new Date(user.uploadDate) > new Date(stats.lastUpload)) {
          stats.lastUpload = user.uploadDate;
        }
      });

      if (users.length > 0) {
        stats.averageSize = stats.totalSize / users.length;
      }

      return stats;
    } catch (error) {
      console.error('❌ Erro ao obter estatísticas:', error);
      return {
        totalUsers: 0,
        totalSize: 0,
        averageSize: 0,
        lastUpload: null,
        fileTypes: {}
      };
    }
  }

  /**
   * Limpa o cache de fotos
   */
  clearCache() {
    this.photoCache.clear();
    console.log('🧹 Cache de fotos limpo');
  }

  /**
   * Obtém informações de performance
   * @returns {Object} Métricas de performance
   */
  getPerformanceMetrics() {
    return {
      cacheSize: this.photoCache.size,
      cacheHitRate: this.photoCache.size > 0 ? 'Ativo' : 'Inativo',
      maxFileSize: `${(this.maxFileSize / (1024 * 1024)).toFixed(1)}MB`,
      allowedTypes: this.allowedTypes.length
    };
  }
}

// Instância singleton do serviço
const userPhotoService = new UserPhotoService();

export default userPhotoService;

